import { useCallback, useEffect, useState } from 'react';
import { isSome } from 'fp-ts/lib/Option';

import { CreateLinkCriteriaBuilder } from 'use-case/link/create/createLinkCriteria';
import { ReadLinksCriteriaBuilder } from 'use-case/link/read/readLinksCriteria';
import { EnableLinkCriteriaBuilder } from 'use-case/link/update/updateLinkCriteria';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { useProfile } from 'adapter/in/common/context/static/Profile';
import { useLinks } from 'adapter/in/dashboards/context/Links';
import Link from 'domain/entity/link';
import { DeleteLinkCriteriaBuilder } from 'use-case/link/delete/deleteLinkCriteria';
import { ReadCSVLocationLinksCriteriaBuilder } from 'use-case/link/read/readCSVLocationLinksCriteria';
import { convertArrayToCSV } from 'adapter/in/dashboards/utils/convertArrayToCSV';
import { generateUrl } from 'adapter/in/dashboards/utils/generateUrl';
import i18n from 'asset/translation';
import { LocationLinkCSV, LocationType } from 'domain/entity/location';

const BASE_URL = window.origin;

const parseTypeToText = (type: LocationType): string => {
    switch (type) {
        case LocationType.ROOT:
            return i18n.t('dashboards.link.bulk.csv.type.root');
        case LocationType.BRANCH:
            return i18n.t('dashboards.link.bulk.csv.type.zone');
        case LocationType.LEAF:
            return i18n.t('dashboards.link.bulk.csv.type.location');
        case LocationType.SECTION:
        default:
            return i18n.t('dashboards.link.bulk.csv.type.sublocation');
    }
};
export const useReadLinks = (): {
    link?: Link;
    isLoading: boolean;
    hasError: boolean;
    handleReadLinks: (_dashboardId: string, _dashboardName: string) => void;
    handleReadLinksAgain: () => void;
    handleEnableLink: (id: string, active: boolean) => void;
    handleDeleteLink: (id: string) => void;
    handleLocations: (id: string) => void;
    reset: () => void;
} => {
    const { profile } = useProfile();
    const { createLink, readLinks, enableLink, deleteLink, readCSVLocationLinks } = useLinks();
    const [dashboardId, setDashboardId] = useState('');
    const [dashboardName, setDashboardName] = useState('');
    const [link, setLink] = useState<Link>();

    const {
        isLoading: isLoadingCreateLink,
        data: createdLink,
        error: errorCreateLink,
        execute: executeCreateLink,
        reset: resetCreateLink,
    } = useCase(createLink, false);
    const { isLoading: isLoadingReadLinks, data: links, error: errorReadLinks, execute: executeReadLinks, reset: resetReadLinks } = useCase(
        readLinks,
        false
    );
    const {
        isLoading: isLoadingEnableLink,
        data: enabledLink,
        error: errorEnableLink,
        execute: executeEnableLink,
        reset: resetEnableLink,
    } = useCase(enableLink, false);

    const {
        isLoading: isLoadingDeleteLink,
        data: deletedLink,
        error: errorDeleteLink,
        execute: executeDeleteLink,
        reset: resetDeleteLink,
    } = useCase(deleteLink, false);

    const {
        isLoading: isLoadingLocationsData,
        data: locationsData,
        error: errorLocationsData,
        execute: executeLocationsData,
        reset: resetLocationsData,
    } = useCase(readCSVLocationLinks, false);

    const handleReadLinks = useCallback((_dashboardId: string, _dashboardName: string) => {
        setDashboardName(_dashboardName);
        setDashboardId(_dashboardId);
    }, []);

    const handleExecuteReadLinks = () => {
        executeReadLinks(ReadLinksCriteriaBuilder.create().withDashboardId(dashboardId).withLocationId(profile.locationId).build());
    };

    const handleEnableLink = (id: string, active: boolean) => {
        executeEnableLink(
            EnableLinkCriteriaBuilder.create()
                .withId(id)
                .withDashboardId(dashboardId)
                .withLocationId(profile.locationId)
                .withActive(active)
                .build()
        );
    };

    const handleDeleteLink = (id: string) => {
        executeDeleteLink(DeleteLinkCriteriaBuilder.create().withId(id).withDashboardId(dashboardId).build());
    };

    const handleLocations = (id: string) => {
        executeLocationsData(ReadCSVLocationLinksCriteriaBuilder.create().withLinkId(id).withDashboardId(dashboardId).build());
    };

    const handleReset = () => {
        resetReadLinks();
        resetCreateLink();
        resetEnableLink();
        resetDeleteLink();
        resetLocationsData();
        setLink(undefined);
        setDashboardId('');
    };

    useEffect(() => {
        if (dashboardId && dashboardId !== '' && profile.locationId !== '') {
            handleExecuteReadLinks();
        }
    }, [dashboardId, profile.locationId]);

    const buildUrl = (_linkedUrl: string) => `${BASE_URL}${_linkedUrl}`;

    useEffect(() => {
        if (isSome(createdLink)) {
            setLink(new Link(createdLink.value.id, buildUrl(createdLink.value.linkedUrl), createdLink.value.active));
        } else if (isSome(links)) {
            if (links.value.length > 0) {
                setLink(new Link(links.value[0].id, buildUrl(links.value[0].linkedUrl), links.value[0].active));
            } else if (profile) {
                executeCreateLink(
                    CreateLinkCriteriaBuilder.create().withDashboardId(dashboardId).withLocationId(profile.locationId).build()
                );
            }
        }
    }, [links, createdLink]);

    useEffect(() => {
        if (isSome(enabledLink)) {
            setLink(new Link(enabledLink.value.id, buildUrl(enabledLink.value.linkedUrl), enabledLink.value.active));
        }
    }, [enabledLink]);

    useEffect(() => {
        if (isSome(deletedLink)) {
            executeCreateLink(CreateLinkCriteriaBuilder.create().withDashboardId(dashboardId).withLocationId(profile.locationId).build());
        }
    }, [deletedLink]);

    useEffect(() => {
        if (isSome(locationsData)) {
            const parsedLocationLinks = (locationsData.value.map((locationLink: LocationLinkCSV) => ({
                name: locationLink.name,
                linkedUrl: buildUrl(locationLink.linkedUrl),
                type: parseTypeToText(locationLink.type),
            })) as unknown) as Record<string, unknown>[];
            const url = generateUrl(
                convertArrayToCSV(parsedLocationLinks, [
                    { key: 'name', label: i18n.t('dashboards.link.bulk.csv.name') },
                    { key: 'type', label: i18n.t('dashboards.link.bulk.csv.type.title') },
                    { key: 'linkedUrl', label: i18n.t('dashboards.link.bulk.csv.linkedUrl') },
                ])
            );
            const linkElement = document.createElement('a');
            linkElement.setAttribute('href', url);
            linkElement.setAttribute('download', `${dashboardName}_links_${profile.locationName}.csv`);
            linkElement.style.visibility = 'hidden';
            document.body.appendChild(linkElement);
            linkElement.click();
            document.body.removeChild(linkElement);
        }
    }, [locationsData]);

    return {
        link,
        isLoading: isLoadingReadLinks || isLoadingCreateLink || isLoadingEnableLink || isLoadingDeleteLink || isLoadingLocationsData,
        hasError:
            isSome(errorReadLinks) ||
            isSome(errorCreateLink) ||
            isSome(errorEnableLink) ||
            isSome(errorDeleteLink) ||
            isSome(errorLocationsData),
        handleReadLinks,
        handleReadLinksAgain: handleExecuteReadLinks,
        handleEnableLink,
        handleDeleteLink,
        handleLocations,
        reset: handleReset,
    };
};
